import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Carousel, CarouselItem, Container, EqualHeightProvider, Heading, Notice, Separator, Spinner, THeadingLevel } from 'telia-front-react';
import GaPushOnMount from '../content/GaPushOnMount';
import ErrorUtil from '../error/ErrorUtil';
import { GaData } from '../stores/GaDataStore';
import PersonalOffersStore from '../stores/PersonalOffersStore';
import ProposalStore from '../stores/ProposalStore';
import SsoStore from '../stores/SsoStore';
import B2BOfferContainer from './B2BOffer/B2BOfferContainer';
import { Offer, OfferActions } from './OfferInterfaces';
import PersonalOffersCard from './PersonalOffersCard';
import ProposalsCard from './proposals/ProposalsCard';

export interface PersonalOffersProps {
  ctaLabel?: string;
  headingLevel?: THeadingLevel;
  b2bMobileFormName?: string;
  b2bInternetFormName?: string;
  salesCampaignId?: string;
  personalOffersStore?: PersonalOffersStore;
  ssoStore?: SsoStore;
  proposalStore?: ProposalStore;
}

const PersonalOffers = (props: PersonalOffersProps): JSX.Element => {
  const { translate } = React.useContext(TranslationsContext);
  const { ctaLabel, headingLevel = 2, b2bMobileFormName, b2bInternetFormName, salesCampaignId, personalOffersStore, ssoStore, proposalStore } = props;
  const offerHeadingLevel = (headingLevel + 1) as THeadingLevel;

  const offerActions: OfferActions = {
    present: async (offer: Offer) => {
      if (!offer.presented) {
        try {
          await personalOffersStore.presentOffer(offer);
          offer.presented = true;
        } catch (e) {
          ErrorUtil.pushErrorToTeliaRumAndLog(e);
        }
      }
    },
    reject: (offer: Offer) => {
      personalOffersStore.offers.data = personalOffersStore.offers.data.filter((o) => o.recommendationItemId !== offer.recommendationItemId);
      personalOffersStore.rejectOffer(offer).catch((e) => ErrorUtil.pushErrorToTeliaRumAndLog(e));
    },
    accept: (recommendationItemId: number) => {
      personalOffersStore.acceptOffer(recommendationItemId).catch((e) => ErrorUtil.pushErrorToTeliaRumAndLog(e));
    }
  };

  React.useEffect(() => {
    personalOffersStore.subscribe();

    return () => {
      personalOffersStore.unsubscribe();
    };
  }, []);

  const getOffersGaEventData = (eventLabel: string): GaData => {
    return {
      event: 'AutoEvent',
      eventCategory: 'Pakkumised',
      eventAction: 'Personal offers',
      eventLabel: eventLabel
    };
  };

  const getCarouselItems = (): JSX.Element[] => {
    const carouselItems = personalOffersStore.sortPersonalOffersByPriority(personalOffersStore.offers.data).map((offer: Offer, index: number) => (
      <CarouselItem key={`carousel-item-${index}`}>
        <PersonalOffersCard headingLevel={offerHeadingLevel} offer={offer} ctaLabel={ctaLabel} offerActions={offerActions} />
      </CarouselItem>
    ));

    if (proposalStore.hasProposals) {
      carouselItems.push(
        <CarouselItem key="carousel-item-proposal">
          <ProposalsCard headingLevel={offerHeadingLevel} />
        </CarouselItem>
      );
    }
    return carouselItems;
  };

  const renderPersonalOffersBlock = (): JSX.Element => {
    const carouselItems = getCarouselItems();
    return (
      <>
        <Heading level={headingLevel} className="h2">
          {translate('personalOffers.heading', { numberOfOffers: carouselItems.length })}
        </Heading>
        <Separator spacing="small" color="invisible" />
        <EqualHeightProvider groups={['offer-content', 'offer-cta']}>
          <Carousel
            breakPoint={personalOffersStore.offers.data.length > 1 ? null : undefined}
            slidesToShow={1}
            nextArrowText={translate('carousel.next')}
            prevArrowText={translate('carousel.previous')}
            align="left"
            responsive={[
              {
                breakPoint: 'sm',
                props: {
                  slidesToShow: 2,
                  arrows: true
                }
              }
            ]}
          >
            {carouselItems}
          </Carousel>
        </EqualHeightProvider>
        {personalOffersStore.hasB2BOffer && <Separator color="invisible" spacing="small" />}
      </>
    );
  };

  const pushNoPersonalOffersGaData = (): JSX.Element => {
    return (
      <GaPushOnMount
        data={getOffersGaEventData(ssoStore.isDealer ? 'Profile 9' : `Show - no personal recommendations - ${ssoStore.isEmployee ? 'employee' : 'customer'}`)}
      />
    );
  };

  const renderPersonalOffersIfHasOffers = (): JSX.Element => {
    if (personalOffersStore.hasOffers || proposalStore.hasProposals) {
      return renderPersonalOffersBlock();
    }
    return pushNoPersonalOffersGaData();
  };

  if (!ssoStore.customerId) {
    return null;
  }

  if (!ssoStore.hasLegalOrServiceTeliaMandate) {
    return (
      <Container>
        <Notice type="error">{translate('personalOffers.error.noMandate')}</Notice>
      </Container>
    );
  }

  if (personalOffersStore.offersFetching || proposalStore.proposalsFetching) {
    return <Spinner />;
  }

  if ((personalOffersStore.offersFetchFailed || proposalStore.recommendationsFetchFailed) && !personalOffersStore.hasOffers && !proposalStore.hasProposals) {
    return (
      <Container>
        {pushNoPersonalOffersGaData()}
        <Notice type="error">{translate('personalOffers.error.fetchFailed')}</Notice>
      </Container>
    );
  }

  return (
    <>
      {renderPersonalOffersIfHasOffers()}
      <Container>
        {personalOffersStore.hasB2BOffer && (
          <B2BOfferContainer
            b2bMobileFormName={b2bMobileFormName}
            b2bInternetFormName={b2bInternetFormName}
            salesCampaignId={+salesCampaignId}
            offerActions={offerActions}
            headingLevel={offerHeadingLevel}
          />
        )}
      </Container>
    </>
  );
};

export default inject('personalOffersStore', 'ssoStore', 'proposalStore')(observer(PersonalOffers));
